import React from 'react';
import { useSelector } from 'react-redux';
import { FaRegClock } from 'react-icons/fa';
import { FiExternalLink } from 'react-icons/fi';
import { BiLogIn, BiLogOut } from 'react-icons/bi';
import styles from './TimeTrackingButton.module.css';

const TimeTrackingButton = ({
  time = '0:00:00',
  onClick,
  loading = false,
  isPlaying = false,
}) => {
  // Get all timer state from Redux
  const timerState = useSelector((state) => ({
    timerSession: state.timer.timerSession,
    selectedProject: state.timer.selectedProject,
    projects: state.timer.projects,
    tasks: state.timer.tasks,
    // COMMENTED OUT: Punch state from Redux
    // punchStatus: state.timer.punchStatus
  }));

  // Helper function to truncate text with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get project and task names from timer session
  const getProjectName = () => {
    if (loading) return 'Processing...';
    if (!timerState.timerSession.projectId) return '';

    // First, try to get project name from session data (most reliable)
    if (timerState.timerSession.projectName) {
      return truncateText(timerState.timerSession.projectName, 25);
    }

    // Try to get project name from selected project in Redux
    if (timerState.selectedProject && timerState.selectedProject.label) {
      return truncateText(timerState.selectedProject.label, 25);
    }

    // Fallback to projects array
    if (timerState.projects && Array.isArray(timerState.projects)) {
      const project = timerState.projects.find(
        (p) => p.id === timerState.timerSession.projectId
      );
      if (project) {
        return truncateText(
          project.name || project.project_name || 'Untitled Project',
          25
        );
      }
    }

    // If timer is running but no project found, show a placeholder
    if (isPlaying) return 'Active Project';

    return '';
  };

  const getTaskName = () => {
    if (loading) return 'Please wait';
    if (!timerState.timerSession.taskId && !timerState.timerSession.taskName)
      return '';

    // Try to get task name from tasks array
    if (timerState.tasks && Array.isArray(timerState.tasks)) {
      const task = timerState.tasks.find(
        (t) => t.id === timerState.timerSession.taskId
      );
      if (task) {
        return truncateText(task.Title, 20);
      }
    }

    // Fallback to taskName from session
    if (timerState.timerSession.taskName) {
      return truncateText(timerState.timerSession.taskName, 20);
    }

    // If timer is running but no task found, show a placeholder
    if (isPlaying) return 'Active Task';

    return '';
  };

  const projectName = getProjectName();
  const taskName = getTaskName();

  return (
    <button
      onClick={onClick}
      className={`${styles.button} ${loading ? styles.disabled : ''} ${isPlaying ? styles.active : ''}`}
      disabled={loading}
      title={loading ? 'Processing...' : 'Open time tracking'}
    >
      {loading ? (
        <div className={styles.loadingSpinner}></div>
      ) : (
        <FaRegClock className={styles.icon} />
      )}
      <span className={styles.timer}>{time}</span>
      {(projectName || taskName) && (
        <div className={styles.info}>
          {projectName && (
            <span className={styles.projectName}>
              {loading ? 'Processing...' : projectName}
            </span>
          )}
          {taskName && (
            <span className={styles.taskName}>
              {loading ? 'Please wait' : taskName}
            </span>
          )}
        </div>
      )}

      {/* COMMENTED OUT: Punch Status Indicator - NEW: Uses daily punch status only */}
      {/*
    <div className={`${styles.punchStatus} ${
      timerState.punchStatus?.dailyPunchStatus?.isPunchedIn 
        ? styles.punchStatusPunchedIn 
        : styles.punchStatusPunchedOut
    }`}>
      {timerState.punchStatus?.dailyPunchStatus?.isPunchedIn ? (
        <BiLogIn className={styles.punchStatusIcon} />
      ) : (
        <BiLogOut className={styles.punchStatusIcon} />
      )}
      <span className={styles.punchStatusText}>
        {timerState.punchStatus?.dailyPunchStatus?.isPunchedIn ? 'In' : 'Out'}
      </span>
    </div>
    */}

      <FiExternalLink className={styles.externalIcon} />
    </button>
  );
};

// Add display name for better debugging
TimeTrackingButton.displayName = 'TimeTrackingButton';

// Export with React.memo for performance optimization
export default React.memo(TimeTrackingButton);
